import { SortType } from '@intelligence/pages/TopCharts/_config/enums';
import type { IGameType } from '@intelligence/types/common';
import type { SteamGamesDetailsOverlapRanksFetchParams } from '@intelligence/pages/TopCharts/PCGames/components/GamesDetails/OverlapSteam/types';
import { OVERLAP_STEAM_ORDERMETRIC } from '@intelligence/pages/TopCharts/PCGames/components/GamesDetails/OverlapSteam/constants/metrics';
import * as filterKeyMap from '@/pages/intelligence/components/Filter/FilterBase/_config';
export const ENTITY_TYPE: IGameType = 'pc';

export const DEFAULT_PAGE_SIZE = 100;
export const DEFAULT_SEGMENT_VALUE = 'played_play';

// ===== 4-segment metrics 配置 =====
export const DEFAULT_METRICS_PLAYED_PLAY_VALUE = [
  'p&p overlap affinity',
  'played & play',
  'avg_playtime',
];
export const DEFAULT_METRICS_PLAYED_WISHLIST_VALUE = [
  'p&w overlap affinity',
  'played & wishlist',
];
export const DEFAULT_METRICS_WISHLISTED_PLAY_VALUE = [
  'w&p overlap affinity',
  'wishlisted & play',
  'avg_playtime',
];
export const DEFAULT_METRICS_WISHLISTED_WISHLIST_VALUE = [
  'w&w overlap affinity',
  'wishlisted & wishlist',
];



// ===== 4-segment metric_filter 配置 =====
export const DEFAULT_METRICS_PLAYED_PLAY_FILTER_VALUE = [
  {
    metric: 'played & play',
    opt: '>',
    value: 20,
  },
];
export const DEFAULT_METRICS_PLAYED_WISHLIST_FILTER_VALUE = [
  {
    metric: 'played & wishlist',
    opt: '>',
    value: 4,
  },
];
export const DEFAULT_METRICS_WISHLISTED_PLAY_FILTER_VALUE = [
  {
    metric: 'wishlisted & play',
    opt: '>',
    value: 20,
  },
];
export const DEFAULT_METRICS_WISHLISTED_WISHLIST_FILTER_VALUE = [
  {
    metric: 'wishlisted & wishlist',
    opt: '>',
    value: 20,
  },
];

// 兼容旧版 played / wishlisted page 语义（供 studio/potentialMarket 等历史调用方继续使用）
export const DEFAULT_METRICS_PLAYED_FILTER_VALUE = [
  ...DEFAULT_METRICS_PLAYED_PLAY_FILTER_VALUE,
  ...DEFAULT_METRICS_PLAYED_WISHLIST_FILTER_VALUE,
];
export const DEFAULT_METRICS_WISHLIST_FILTER_VALUE = [
  ...DEFAULT_METRICS_WISHLISTED_PLAY_FILTER_VALUE,
  ...DEFAULT_METRICS_WISHLISTED_WISHLIST_FILTER_VALUE,
];



// 4-segment metrics 映射
export const SEGMENT_METRICS_MAP: Record<string, string[]> = {
  played_play: DEFAULT_METRICS_PLAYED_PLAY_VALUE,
  played_wishlist: DEFAULT_METRICS_PLAYED_WISHLIST_VALUE,
  wishlisted_play: DEFAULT_METRICS_WISHLISTED_PLAY_VALUE,
  wishlisted_wishlist: DEFAULT_METRICS_WISHLISTED_WISHLIST_VALUE,
};

// 4-segment metric_filter 映射
export const SEGMENT_METRIC_FILTER_MAP: Record<string, any[]> = {
  played_play: DEFAULT_METRICS_PLAYED_PLAY_FILTER_VALUE,
  played_wishlist: DEFAULT_METRICS_PLAYED_WISHLIST_FILTER_VALUE,
  wishlisted_play: DEFAULT_METRICS_WISHLISTED_PLAY_FILTER_VALUE,
  wishlisted_wishlist: DEFAULT_METRICS_WISHLISTED_WISHLIST_FILTER_VALUE,
};

// 4-segment -> page_name 映射
export const SEGMENT_TO_PAGE_NAME: Record<string, string> = {
  played_play: 'played',
  played_wishlist: 'played',
  wishlisted_play: 'wishlisted',
  wishlisted_wishlist: 'wishlisted',
};

// 4-segment 选项列表
export const OVERLAP_SEGMENT_OPTIONS = [
  { text: 'dbi_overlapsteam_segment_played_play', value: 'played_play' },
  { text: 'dbi_overlapsteam_segment_played_wishlist', value: 'played_wishlist' },
  { text: 'dbi_overlapsteam_segment_wishlisted_play', value: 'wishlisted_play' },
  { text: 'dbi_overlapsteam_segment_wishlisted_wishlist', value: 'wishlisted_wishlist' },
];

// 各 segment 对应的 metric_filter buttonText 国际化 key
export const SEGMENT_FILTER_BUTTON_TEXT: Record<string, string> = {
  played_play: 'dbi_overlapsteam_played_play_percent',
  played_wishlist: 'dbi_overlapsteam_played_wishlist_percent',
  wishlisted_play: 'dbi_overlapsteam_wishlisted_play_percent',
  wishlisted_wishlist: 'dbi_overlapsteam_wishlisted_wishlist_percent',
};

// 各 segment 是否展示 avg_playtime
export const SEGMENT_HAS_AVG_PLAYTIME: Record<string, boolean> = {
  played_play: true,
  played_wishlist: false,
  wishlisted_play: true,
  wishlisted_wishlist: false,
};

export const DEFAULT_FETCH_PARAMS: SteamGamesDetailsOverlapRanksFetchParams = {
  /* -----页面其他参数----- */
  id: '',
  /* -----排序、分页参数----- */
  order_metric: OVERLAP_STEAM_ORDERMETRIC[DEFAULT_SEGMENT_VALUE],
  order: SortType.Desc,
  page: 1,
  page_size: 50,
  /* -----排序、分页参数----- */

  /* -----筛选器参数----- */
  engagement_level_a: '2h',
  engagement_level_b: '2h',
  metrics: DEFAULT_SEGMENT_VALUE,
};

// 筛选器value默认值
export const DEFAULTFILTERVALUE = {
  [filterKeyMap.OVERLAPSTEAM_PLAYERS]: {
    value: 'played_play',
  },
  [filterKeyMap.ENGAGELEVEL_SEGEMENT_TWO]: {
    value: {
      engagementLevelA: '2h',
      engagementLevelB: '2h',
    },
  },
  [filterKeyMap.REGION_SEGMENT]: {
    value: 'global',
  },
  [filterKeyMap.REVENUE_GROWTH]: {
    value: { opt: '>', value: 20 },
  },
  [filterKeyMap.ENGAGELEVEL_SEGEMENT]: {
    value: '2h',
  },
  [filterKeyMap.STEAM_TAGS]: {
    value: [],
    logic: 'AND',
  },
  [filterKeyMap.GENRETOR]: {
    source: 'iegg',
    mainGenres: [],
    subGenres: [],
  },
};

// 筛选器自定义布局列表
export const FILTERKEYLIST = [
  filterKeyMap.ROW,
  filterKeyMap.LEFT,
  filterKeyMap.OVERLAPSTEAM_PLAYERS,
  filterKeyMap.ENGAGELEVEL_SEGEMENT_TWO,
  filterKeyMap.ENGAGELEVEL_SEGEMENT,
  filterKeyMap.REGION_SEGMENT,
  filterKeyMap.RIGHT,
  filterKeyMap.TIPICON,
  filterKeyMap.ROW,
  filterKeyMap.LEFT,
  filterKeyMap.STEAM_TAGS,
  filterKeyMap.GENRETOR,
  filterKeyMap.REGION_COUNTRY_SELECTOR,
  filterKeyMap.REVENUE_GROWTH,
  filterKeyMap.RIGHT,
  filterKeyMap.DOWNLOAD,
];

// 筛选器list配置
export const DEFAULTINPUTLIST = [
  {
    savePopupVisble: 0,
    key: filterKeyMap.ROW,
  },
  {
    savePopupVisble: 0,
    key: filterKeyMap.LEFT,
  },
  {
    savePopupVisble: 1,
    key: filterKeyMap.OVERLAPSTEAM_PLAYERS,
  },
  {
    savePopupVisble: 0,
    key: filterKeyMap.RIGHT,
  },
  {
    savePopupVisble: 0,
    key: filterKeyMap.TIPICON,
    content: 'dbi_overlapsteam_continuously_updated',
    hasLeftText: 'dbi_overlapsteam_data_frequency',
    placementValue: 'left',
    hasMarginLeft: true,
  },
  {
    savePopupVisble: 0,
    key: filterKeyMap.ROW,
  },
  {
    savePopupVisble: 0,
    key: filterKeyMap.LEFT,
  },
  {
    savePopupVisble: 1,
    key: filterKeyMap.ENGAGELEVEL_SEGEMENT_TWO,
    visibleinlist: 1,
  },
  {
    savePopupVisble: 1,
    key: filterKeyMap.ENGAGELEVEL_SEGEMENT,
    visibleinlist: 2,
  },
  {
    savePopupVisble: 1,
    key: filterKeyMap.STEAM_TAGS,
    style: { width: 300 },
  },
  {
    savePopupVisble: 1,
    key: filterKeyMap.GENRETOR,
    genre_source: 'iegg',
    disableAnnie: true,
  },
  {
    savePopupVisble: 1,
    key: filterKeyMap.REGION_COUNTRY_SELECTOR,
  },
  {
    savePopupVisble: 1,
    key: filterKeyMap.REVENUE_GROWTH,
  },
  {
    savePopupVisble: 0,
    key: filterKeyMap.RIGHT,
  },
  {
    savePopupVisble: 0,
    key: filterKeyMap.DOWNLOAD,
  },
];
